import { useCallback, useEffect, useRef, useState, type ReactNode, type TouchEvent } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  onClose: () => void;
  children: ReactNode;
  /** Extra classes for the panel (e.g. max-w-lg) */
  panelClassName?: string;
  /** Center dialog on desktop; bottom sheet on mobile */
  variant?: 'center' | 'sheet';
}

const DISMISS_DRAG_PX = 72;
const MOBILE_SHEET_MQ = '(max-width: 639px)';

const Modal = ({ onClose, children, panelClassName = '', variant = 'sheet' }: ModalProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const overlayDismissPointerId = useRef<number | null>(null);
  const dragStartY = useRef(0);
  const viewportFrozenRef = useRef(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const scrollY = window.scrollY;
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = prevWidth;
      window.scrollTo(0, scrollY);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    if (variant !== 'sheet') return undefined;

    const viewport = window.visualViewport;
    if (!viewport) return undefined;

    const syncViewportHeight = () => {
      if (viewportFrozenRef.current) return;
      document.documentElement.style.setProperty('--modal-vvh', `${viewport.height}px`);
    };

    syncViewportHeight();
    viewport.addEventListener('resize', syncViewportHeight);
    viewport.addEventListener('scroll', syncViewportHeight);

    return () => {
      viewport.removeEventListener('resize', syncViewportHeight);
      viewport.removeEventListener('scroll', syncViewportHeight);
      document.documentElement.style.removeProperty('--modal-vvh');
    };
  }, [variant]);

  const handleSelectFocus = () => {
    viewportFrozenRef.current = true;
  };

  const handleSelectBlur = () => {
    viewportFrozenRef.current = false;
    const viewport = window.visualViewport;
    if (viewport) {
      document.documentElement.style.setProperty('--modal-vvh', `${viewport.height}px`);
    }
  };

  const canSwipeDismiss = useCallback(() => {
    if (variant !== 'sheet') return false;
    return window.matchMedia(MOBILE_SHEET_MQ).matches;
  }, [variant]);

  const getScrollEl = () => panelRef.current?.querySelector<HTMLElement>('.modal-sheet-scroll');

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    if (!canSwipeDismiss()) return;

    const target = e.target as HTMLElement;
    const fromHandle = Boolean(target.closest('.modal-sheet-handle'));
    const scrollEl = getScrollEl();
    const atTop = !scrollEl || scrollEl.scrollTop <= 0;

    if (!fromHandle && !atTop) return;

    dragStartY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    const delta = e.touches[0].clientY - dragStartY.current;
    setDragOffset(Math.max(0, delta));
  };

  const endDrag = () => {
    if (dragOffset >= DISMISS_DRAG_PX) {
      onClose();
    }
    setDragOffset(0);
    setIsDragging(false);
  };

  const overlayClass = variant === 'sheet' ? 'modal-overlay modal-overlay-sheet' : 'modal-overlay';
  const panelClass = variant === 'sheet' ? 'modal-panel modal-panel-sheet' : 'modal-panel';

  const panelStyle =
    dragOffset > 0
      ? {
          transform: `translateY(${dragOffset}px)`,
          transition: isDragging ? 'none' : undefined,
        }
      : undefined;

  return createPortal(
    <div
      className={overlayClass}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        overlayDismissPointerId.current =
          e.target === e.currentTarget ? e.pointerId : null;
      }}
      onPointerUp={(e) => {
        const dismissPointerId = overlayDismissPointerId.current;
        overlayDismissPointerId.current = null;
        if (
          dismissPointerId !== null &&
          dismissPointerId === e.pointerId &&
          e.target === e.currentTarget
        ) {
          onClose();
        }
      }}
      onPointerCancel={() => {
        overlayDismissPointerId.current = null;
      }}
      role="presentation"
    >
      <div
        ref={panelRef}
        className={`${panelClass} ${panelClassName}`.trim()}
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={endDrag}
        onTouchCancel={endDrag}
        role="dialog"
        aria-modal="true"
      >
        {variant === 'sheet' && (
          <div className="modal-sheet-handle" aria-hidden>
            <span />
          </div>
        )}
        <div
          className={variant === 'sheet' ? 'modal-sheet-scroll' : undefined}
          onFocusCapture={(e) => {
            if ((e.target as HTMLElement).tagName === 'SELECT') handleSelectFocus();
          }}
          onBlurCapture={(e) => {
            if ((e.target as HTMLElement).tagName === 'SELECT') handleSelectBlur();
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
